import { Bugfender } from '@bugfender/sdk';
import { Alert } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ReactNode, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';

import { enableRefreshUser, setLoggedInUser } from '@/redux/auth/actions';
import { selectAuthState } from '@/redux/auth/selectors';
import { selectRightStartFeature, setTenant } from '@/redux/configs';
import {
  fetchUserHolders,
  selectUserData,
  setUserData,
} from '@/redux/user-data';

import { Language } from '@/types/i18n';

import { getAPIErrorMessage, getHttpResponseCode } from '@/utils/api';
import { isAdultUser } from '@/utils/user';

import { Icon } from '../Icon/Icon';
import { FullPageLoaderOverlay } from '../Layout/FullPageLoader';
import { PublicPage } from './PublicPage';

import { FullPageCenter } from '../Layout/Layout.styled';

type AppRouterProps = {
  protectedPage: ReactNode;
  publicPage: ReactNode;
};

export function AppRouter({
  protectedPage,
  publicPage,
}: Readonly<AppRouterProps>) {
  const dispatch = useAppDispatch();
  useSessionTimeout();

  const { user, initialized, shouldRefreshUser, refreshUserDisabled } =
    useAppSelector(selectAuthState);
  const { fetchingUserHolders } = useAppSelector(selectUserData);
  const isAdultUserFlag = isAdultUser(user);
  const enabledRightStartFeature = useAppSelector(selectRightStartFeature);

  const { data, refetch, error } = useGetCurrentUser({
    enabled: !refreshUserDisabled,
  });

  useEffect(() => {
    if (user?.operationId && enabledRightStartFeature && isAdultUserFlag) {
      dispatch(fetchUserHolders(user?.operationId));
    }
  }, [dispatch, isAdultUserFlag, user?.operationId, enabledRightStartFeature]);

  useEffect(() => {
    if (!shouldRefreshUser) {
      return;
    }
    if (refreshUserDisabled) {
      dispatch(enableRefreshUser());
      return;
    }
    void refetch();
  }, [shouldRefreshUser, refreshUserDisabled, dispatch, refetch]);

  useEffect(() => {
    if (data && !error) {
      const userTenant = data.user.tenant;
      dispatch(
        setTenant({
          tenant: userTenant,
          language: data.user.language as Language,
        }),
      );
      dispatch(setLoggedInUser(data.user));
      dispatch(
        setUserData({
          user: data.user,
          requiredSections: data.requiredSections,
        }),
      );
      Bugfender.setDeviceKey('operationId', data.user.operationId || 'unknown');
      Bugfender.log(`clientType: ${data.user.type || 'unknown'}`);
    }
  }, [data, error, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(setLoggedInUser());
    }
  }, [dispatch, error]);

  const showLoading = !initialized || fetchingUserHolders;

  if (showLoading) {
    return (
      <FullPageLoaderOverlay
        bgColor={(theme: Theme) => theme.palette.background.paper}
      />
    );
  }

  if (user) return protectedPage;

  if (error && ![401, 403].includes(getHttpResponseCode(error))) {
    return (
      <FullPageCenter>
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {getAPIErrorMessage(error)}
        </Alert>
      </FullPageCenter>
    );
  }

  return <PublicPage>{publicPage}</PublicPage>;
}
